import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PencilIcon,
  PrinterIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  BeakerIcon,
  DocumentIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon as HistoryIcon,
} from "@heroicons/react/24/outline";

import { chemicalInventoryApi } from "../../../../../api/chemical-inventory";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import { TinyDocumentSection } from "../../../../../components/dashboard/documents/tinyDocumentSection";
import { NotesDeluxeSection } from "../../../../../components/dashboard/notes/notesDeluxeSection";
import { ChemicalInventoryForm } from "../../../../../components/dashboard/quality/chemical-inventory-form";

/**
 * HSE/KS Chemical Inventory View - Stoffkartotek detaljer (ISO 14001, ISO 45001)
 */
export const HseKsChemicalInventoryView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [chemical, setChemical] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname;
    if (path.includes("/matrix")) return "matrix";
    if (path.includes("/sja")) return "sja";
    if (path.includes("/climate")) return "climate";
    if (path.includes("/chemical-inventory")) return "chemical-inventory";
    if (path.includes("/protective-equipment")) return "protective-equipment";
    return "register";
  });

  const tabs = [
    { id: "register", label: "Risikoregister", path: "/dashboard/hse-ks/risk/register" },
    { id: "matrix", label: "Risikomatrise", path: "/dashboard/hse-ks/risk/matrix" },
    { id: "sja", label: "SJA", path: "/dashboard/hse-ks/risk/sja" },
    { id: "climate", label: "Klimarisiko", path: "/dashboard/hse-ks/risk/climate" },
    { id: "chemical-inventory", label: "Stoffkartotek", path: "/dashboard/hse-ks/risk/chemical-inventory" },
    { id: "protective-equipment", label: "Vernerunder", path: "/dashboard/hse-ks/risk/protective-equipment" },
  ];

  const fetchChemical = async () => {
    try {
      setLoading(true);
      const response = await chemicalInventoryApi.getChemicalInventory(id);
      setChemical(response.data);
    } catch (err) {
      console.error("Error fetching chemical inventory:", err);
      setError(true);
      toast.error(t("Kunne ikke laste stoffkartotek"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChemical();
  }, [id]);

  const handleDelete = async () => {
    if (window.confirm(t("Er du sikker på at du vil slette dette stoffet?"))) {
      try {
        await chemicalInventoryApi.deleteChemicalInventory(id);
        toast.success(t("Stoffkartotek ble slettet"));
        navigate("/dashboard/hse-ks/risk/chemical-inventory");
      } catch (err) {
        toast.error(t("Kunne ikke slette stoffkartotek"));
      }
    }
  };

  const handlePrint = async () => {
    // TODO: Implement print functionality
    toast.info(t("Print funksjonalitet kommer snart"));
  };

  const getHmsLevelIcon = (level) => {
    switch (level) {
      case 1:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 2:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 3:
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      case 4:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <BeakerIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case "active":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("chemical.inventory.status.active")}
          </span>
        );
      case "inactive":
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {t("chemical.inventory.status.inactive")}
          </span>
        );
      case "archived":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("chemical.inventory.status.archived")}
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const getHmsLevelBadge = (level) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (level) {
      case 1:
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("chemical.inventory.hms_level.1")}
          </span>
        );
      case 2:
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("chemical.inventory.hms_level.2")}
          </span>
        );
      case 3:
        return (
          <span className={`${baseClasses} bg-orange-100 text-orange-800`}>
            {t("chemical.inventory.hms_level.3")}
          </span>
        );
      case 4:
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            {t("chemical.inventory.hms_level.4")}
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error || !chemical) {
    return <ResourceError errors={["Kunne ikke laste stoffkartotek"]} />;
  }

  return (
    <>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Navigation Tabs */}
        <div className="bg-white shadow sm:rounded-lg mb-6">
          <div className="border-b border-gray-200 px-4 py-3 sm:px-6">
            <nav className="flex space-x-4 overflow-x-auto" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    navigate(tab.path);
                  }}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

          {/* Basic Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getHmsLevelIcon(chemical.hms_level)}
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {chemical.name}
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                      {t("chemical.inventory.cas_number")}:{" "}
                      {chemical.cas_number}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusBadge(chemical.status)}
                  {getHmsLevelBadge(chemical.hms_level)}
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.chemical_formula")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.chemical_formula || t("Ikke spesifisert")}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.molecular_weight")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.molecular_weight || t("Ikke spesifisert")}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.purity")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.purity || t("Ikke spesifisert")}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.version")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.version}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Safety Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t("chemical.inventory.safety_info")}
              </h3>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.hazard_statements")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.hazard_statements || t("Ikke spesifisert")}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.precautionary_statements")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.precautionary_statements || t("Ikke spesifisert")}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.signal_word")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.signal_word || t("Ikke spesifisert")}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Safety Instructions */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t("chemical.inventory.safety_instructions")}
              </h3>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.general_use")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.general_use || t("Ikke spesifisert")}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.fire_measures")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.fire_measures || t("Ikke spesifisert")}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.spill_measures")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.spill_measures || t("Ikke spesifisert")}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.first_aid")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.first_aid || t("Ikke spesifisert")}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.personal_protection")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.personal_protection || t("Ikke spesifisert")}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Supplier Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t("chemical.inventory.supplier_info")}
              </h3>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.supplier_name")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.supplier_name || t("Ikke spesifisert")}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.supplier_address")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.supplier_address || t("Ikke spesifisert")}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.emergency_phone")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.emergency_phone || t("Ikke spesifisert")}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <DocumentIcon className="h-5 w-5 mr-2" />
                {t("chemical.inventory.documents")}
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <TinyDocumentSection
                title={t("chemical.inventory.documents")}
                documents={
                  chemical.chemical_inventory_documents?.map(
                    (doc) => doc.document
                  ) || []
                }
                onDocumentClick={(document) => {
                  // Handle document click
                }}
              />
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                {t("chemical.inventory.notes")}
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <NotesDeluxeSection
                title={t("chemical.inventory.notes")}
                notes={chemical.chemical_inventory_notes || []}
                onNoteSubmit={(noteData) => {
                  // Handle note submission
                }}
              />
            </div>
          </div>

          {/* History */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <HistoryIcon className="h-5 w-5 mr-2" />
                {t("chemical.inventory.history")}
              </h3>
            </div>
            <div className="border-t border-gray-200">
              <ul className="divide-y divide-gray-200">
                {chemical.chemical_inventory_histories?.map((history) => (
                  <li key={history.id} className="px-4 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {history.action}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {history.user_name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(history.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {t("Versjon")} {history.version}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
      </div>

      {/* Edit Dialog */}
      <ChemicalInventoryForm
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        chemicalInventory={chemical}
        onSuccess={() => {
          fetchChemical();
          setShowEditDialog(false);
        }}
      />
    </>
  );
};
